/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import colors from '@sportninja/common/constants/appColors'
import {
  GAMES_PER_BUCKET_TITLE,
  MAX_SELECTED_STATS,
  PROGRESS_RANGES,
  RANGE_LABELS,
} from '@sportninja/common/features/profile/stats/constants'
import type {
  ProgressSectionData,
  SectionState,
} from '@sportninja/common/features/profile/stats/hooks/usePlayerStatsTab'
import type {
  StatsSelection,
  StatsSelectionAction,
} from '@sportninja/common/features/profile/stats/types'
import {
  buildProgressTiles,
  sumGamesPlayed,
} from '@sportninja/common/features/profile/stats/utils/progressTiles'
import { buildGamesPlayedChart } from '@sportninja/common/features/profile/stats/utils/gamesPlayedChart'
import {
  getSeriesChipColors,
  getSeriesColor,
} from '@sportninja/common/features/profile/stats/utils/seriesColors'
import { isStatLimitReached } from '@sportninja/common/features/profile/stats/utils/selection'
import { getStatName } from '@sportninja/common/features/profile/stats/utils/statNames'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import ChipGroup from 'src/components/ts-components/ChipGroup'
import EmptyState from 'src/components/ts-components/EmptyState'
import SelectTab from 'src/components/ts-components/SelectTab'
import Text from 'src/components/ts-components/Text'

import ProgressChart from './ProgressChart'
import ProgressTiles from './ProgressTiles'
import ProgressCardSkeleton from './skeletons/ProgressCardSkeleton'

interface ProgressCardProps {
  progress: SectionState<ProgressSectionData>
  selection: StatsSelection
  dispatch: (action: StatsSelectionAction) => void
  isAllSports: boolean
  /** Abbr → full stat name, for the stat chips and the stat tile. */
  statNames: Record<string, string>
}

const RANGE_OPTIONS = PROGRESS_RANGES.map((range) => ({
  value: range,
  label: RANGE_LABELS[range],
}))

/** The All Sports line plots games played itself. */
const GAMES_ABBR = 'GP'

const SPORT_SUBTITLE =
  'How this player’s stats change over time · pick a stat and a range'
const ALL_SPORTS_SUBTITLE = 'Games played across every sport over time'
const STAT_LIMIT_TOOLTIP = `Up to ${MAX_SELECTED_STATS} stats at once`

/** Section title + range switch over a card of tiles, stat chips and the trend chart. */
const ProgressCard = ({
  progress,
  selection,
  dispatch,
  isAllSports,
  statNames,
}: ProgressCardProps) => {
  'use memo'

  const data = progress.data
  const totalGames = data ? sumGamesPlayed(data.gamesChart) : 0
  const chartStats = data?.chartStats ?? []
  const defaultStats = data?.defaultStats ?? []
  // At the limit, the unselected chips are disabled — every colour is taken.
  const isAtStatLimit = isStatLimitReached(selection.stats)
  const statOptions = chartStats.map((abbr) => {
    const isLocked = isAtStatLimit && !selection.stats.includes(abbr)
    return {
      value: abbr,
      label: getStatName(statNames, abbr),
      disabled: isLocked,
      tooltip: isLocked ? STAT_LIMIT_TOOLTIP : undefined,
    }
  })

  const handleRangeChange = (value: string) => {
    const range = PROGRESS_RANGES.find((r) => r === value)
    if (range) {
      dispatch({ type: 'setRange', range })
    }
  }

  const handleStatToggle = (stat: string) => {
    dispatch({ type: 'toggleStat', stat, chartStats, defaultStats })
  }

  // A selected chip wears its line's colour, by the stat's colour slot.
  const statChipColor = (stat: string) =>
    getSeriesChipColors(selection.colorSlots[stat], 'dark')

  const renderChart = () => {
    if (!data) {
      return null
    }
    if (totalGames === 0) {
      return (
        <div css={emptyWrapStyles}>
          <EmptyState
            title={capitalizeWords('No Games In This Range')}
            subtitle='Try a longer range.'
            minHeight='280px'
          />
        </div>
      )
    }
    if (isAllSports) {
      return (
        <ProgressChart
          // Remount on a new range so a stale point selection clears.
          key={`all:${selection.range}`}
          series={[
            {
              abbr: GAMES_ABBR,
              chart: buildGamesPlayedChart(data.gamesChart),
              color: colors.PRIMARY,
            },
          ]}
          heading={GAMES_PER_BUCKET_TITLE[data.bucket]}
          showGamesStrip={false}
        />
      )
    }
    if (data.charts.length > 0) {
      return (
        <ProgressChart
          // Remount on new stats or range so a stale point selection clears.
          key={`${selection.stats.join(',')}:${selection.range}`}
          series={data.charts.map(({ abbr, colorSlot, chart }) => ({
            abbr,
            name: getStatName(statNames, abbr),
            chart,
            color: getSeriesColor(colorSlot, 'dark'),
          }))}
        />
      )
    }
    return null
  }

  const renderBody = () => {
    if (progress.isLoading) {
      return <ProgressCardSkeleton />
    }
    if (!data) {
      return progress.error ? (
        <Text variant='paragraphSmall' color={colors.ERROR_200}>
          Could not load progress.
        </Text>
      ) : null
    }
    return (
      <>
        <ProgressTiles
          tiles={buildProgressTiles({
            progress: data,
            abbr: isAllSports ? null : selection.stats[0] ?? null,
            range: selection.range,
            isAllSports,
            statNames,
          })}
        />
        {isAllSports ? (
          <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
            Pick a sport to chart stats
          </Text>
        ) : null}
        {!isAllSports && statOptions.length > 0 ? (
          <ChipGroup
            options={statOptions}
            values={selection.stats}
            onChange={handleStatToggle}
            selectedColor={statChipColor}
            ariaLabel='Stats to chart'
          />
        ) : null}
        {renderChart()}
      </>
    )
  }

  return (
    <section css={sectionStyles}>
      <div css={headerStyles}>
        <div css={titleStyles}>
          <Text variant='heading5' weight='semiBold' color={colors.WHITE}>
            {capitalizeWords('Progress')}
          </Text>
          <Text variant='paragraphSmall' color={colors.NEUTRAL_50}>
            {isAllSports ? ALL_SPORTS_SUBTITLE : SPORT_SUBTITLE}
          </Text>
        </div>
        <SelectTab
          options={RANGE_OPTIONS}
          value={selection.range}
          onChange={handleRangeChange}
        />
      </div>
      <div css={cardStyles}>{renderBody()}</div>
    </section>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

/** Same rhythm as the Recent Games and season stats sections. */
const sectionStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const headerStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 8px;
`

const titleStyles = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const cardStyles = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  border: 1px solid ${colors.SECONDARY_300};
  border-radius: 8px;
  background: ${colors.SECONDARY_400};
`

const emptyWrapStyles = css`
  border: 1px dashed ${colors.SECONDARY_200};
  border-radius: 8px;
`

export default ProgressCard
